import React, { useState, useEffect } from 'react';
import { ChromePicker } from 'react-color';
import { QRCodeCanvas } from 'qrcode.react';
import { useHistory } from 'react-router-dom';
import { supabase } from '../../../helpers/Supabase';
import Loader from '../../Loader/Loader';
import '../../../styles/components/QRCode.scss';

const QRCodeComponent: React.FC = () => {
  const history = useHistory();

  const [clientName, setClientName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [headline, setHeadline] = useState("Scan the QR Code");
  const [overlayMode, setOverlayMode] = useState<"solid" | "gradient">("solid");
  const [solidColor, setSolidColor] = useState("rgba(35, 0, 150, 0.6)");
  const [gradientStart, setGradientStart] = useState("rgba(27, 255, 194, 0.8)");
  const [gradientEnd, setGradientEnd] = useState("rgba(36, 151, 149, 0.8)");
  const [bgType, setBgType] = useState<"image" | "video">("image");
  const [bgSourceType, setBgSourceType] = useState<"url" | "upload">("url");
  const [bgUrl, setBgUrl] = useState("https://w0.peakpx.com/wallpaper/761/22/HD-wallpaper-blue-background-1-computer-technology-art-background-painting-wide-screen-computer-graphics-abstract-artwork.jpg");
  const [bgFile, setBgFile] = useState<File | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [isActualFullscreen, setIsActualFullscreen] = useState(false);
  const [logoSourceType, setLogoSourceType] = useState<"url" | "upload">("url");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fontColor, setFontColor] = useState("#ffffff");
  const [qrColor, setQrColor] = useState("#ffffff");
  const [stores, setStores] = useState<any[]>([]);
  const [selectedStore, setSelectedStore] = useState<any | null>(null);
  
  // New state for toolbar
  const [activeTool, setActiveTool] = useState<string>('content');
  const [showColorPicker, setShowColorPicker] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showExportTools, setShowExportTools] = useState(false);
  const [bitlyLink, setBitlyLink] = useState<string>('');
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [showPreviewControls, setShowPreviewControls] = useState(false);

  useEffect(() => {
    const checkAuthAndFetchClient = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          history.push('');
          return;
        }

        const { data: userRows } = await supabase
          .from('users')
          .select('client_id')
          .eq('auth_user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1);

        const userRow = userRows && userRows.length > 0 ? userRows[0] : null;

        if (userRow?.client_id) {
          const { data: client } = await supabase
            .from('clients')
            .select('name, logo_url')
            .eq('id', userRow.client_id)
            .single();

          if (client) {
            setClientName(client.name);
            setLogoUrl(client.logo_url);
          }

          const { data: storeList } = await supabase
            .from('stores')
            .select('*')
            .eq('client_id', userRow.client_id)
            .eq('is_active', true);

          setStores(storeList || []);
          if (storeList?.length) {
            setSelectedStore(storeList[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching client info:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthAndFetchClient();
  }, [history]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setFullscreen(false);
        setIsActualFullscreen(false);
        setShowColorPicker(null);
        // Exit actual fullscreen if active
        if (document.fullscreenElement) {
          document.exitFullscreen();
        }
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  // Cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      // Cleanup any pending timeouts or intervals
      setShowPreviewControls(false);
      setShowColorPicker(null);
      setShowExportTools(false);
      setFullscreen(false);
      setIsActualFullscreen(false);
    };
  }, []);

  // Handle preview controls visibility with timeout
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const handleMouseMove = () => {
      setShowPreviewControls(true);
      
      // Clear existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      // Hide controls after 3 seconds of no movement
      timeoutId = setTimeout(() => {
        setShowPreviewControls(false);
      }, 3000);
    };

    // Initial timeout
    timeoutId = setTimeout(() => {
      setShowPreviewControls(false);
    }, 3000);

    document.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  useEffect(() => {
    const loadDesign = async () => {
      if (!selectedStore) return;

      if (selectedStore.design_config) {
        const config = selectedStore.design_config;
        setHeadline(config.headline || "Scan the QR Code");
        setOverlayMode(config.overlayMode || "solid");
        setSolidColor(config.solidColor || "rgba(35, 0, 150, 0.6)");
        setGradientStart(config.gradientStart || "rgba(27, 255, 194, 0.8)");
        setGradientEnd(config.gradientEnd || "rgba(36, 151, 149, 0.8)");
        setBgType(config.bgType || "image");
        setBgSourceType(config.bgSourceType || "url");
        setBgUrl(config.bgUrl || "https://w0.peakpx.com/wallpaper/761/22/HD-wallpaper-blue-background-1-computer-technology-art-background-painting-wide-screen-computer-graphics-abstract-artwork.jpg");
        setLogoSourceType(config.logoSourceType || "url");
        setLogoUrl((prev) => config.logoUrl || prev || "");
        setFontColor(config.fontColor || "#ffffff");
        setQrColor(config.qrColor || "#ffffff");
        return;
      }

      if (selectedStore.industry) {
        const { data: templates } = await supabase
          .from("industry_templates")
          .select("*")
          .eq("industry", selectedStore.industry)
          .eq("flow_type", "check-in")
          .order('created_at', { ascending: false })
          .limit(1);

        const template = templates && templates.length > 0 ? templates[0] : null;

        if (template) {
          setHeadline(template.greeting || "Scan the QR Code");
          setOverlayMode(template.overlay_mode || "solid");
          setSolidColor(template.solid_color || "rgba(35, 0, 150, 0.6)");
          setGradientStart(template.gradient_start || "rgba(27, 255, 194, 0.8)");
          setGradientEnd(template.gradient_end || "rgba(36, 151, 149, 0.8)");
          setBgType(template.bg_type || "image");
          setBgSourceType("url");
          setBgUrl(template.bg_url || "https://w0.peakpx.com/wallpaper/761/22/HD-wallpaper-blue-background-1-computer-technology-art-background-painting-wide-screen-computer-graphics-abstract-artwork.jpg");
          setLogoSourceType("url");
          setLogoUrl((prev) => template.logo_url || prev || "");
          setFontColor(template.font_color || "#ffffff");
          setQrColor(template.qr_color || "#ffffff");
        }
      }
    };

    loadDesign();
  }, [selectedStore]);

  const handleStoreChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const store = stores.find((s) => s.id === e.target.value);
    setSelectedStore(store || null);
  };

  const getOverlayStyle = () => {
    if (overlayMode === "solid") return solidColor;
    return `linear-gradient(to right, ${gradientStart}, ${gradientEnd})`;
  };

  const getBackgroundSource = () => {
    if (bgSourceType === "upload" && bgFile) {
      return URL.createObjectURL(bgFile);
    }
    return bgUrl;
  };

  const getLogoSource = () => {
    if (logoSourceType === "upload" && logoFile) {
      return URL.createObjectURL(logoFile);
    }
    return logoUrl;
  };

  const handleSave = async () => {
    if (!selectedStore?.id) {
      alert("No store selected. Please select a store first.");
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("stores")
        .upsert({
          id: selectedStore.id,
          store_name: selectedStore.store_name,
          client_id: selectedStore.client_id,
          hash_id: selectedStore.hash_id,
          industry: selectedStore.industry,
          is_active: selectedStore.is_active,
          design_config: {
            headline,
            overlayMode,
            solidColor,
            gradientStart,
            gradientEnd,
            bgType,
            bgSourceType,
            bgUrl,
            logoSourceType,
            logoUrl,
            fontColor,
            qrColor,
          }
        }, {
          onConflict: 'id'
        });

      if (error) {
        console.error("Error saving design:", error);
        alert("Failed to save design. Please try again.");
      } else {
        // Show success animation
        const saveBtn = document.querySelector('.save-btn');
        if (saveBtn) {
          saveBtn.classList.add('saved');
          setTimeout(() => saveBtn.classList.remove('saved'), 2000);
        }
      }
    } catch (err) {
      console.error("Unexpected error:", err);
      alert("An unexpected error occurred. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const downloadQRCode = () => {
    const canvas = document.querySelector('canvas');
    if (canvas) {
      const link = document.createElement('a');
      link.download = `vivvy-qr-${selectedStore?.store_name || 'checkin'}.png`;
      link.href = canvas.toDataURL();
      link.click();
    }
  };

  const generateBitlyLink = async () => {
    if (!selectedStore?.hash_id) return;
    
    setIsGeneratingLink(true);
    try {
      // For now, we'll create a simple shortened URL
      // In production, you'd integrate with Bitly API
      const shortUrl = `https://vivvy.ai/c/${selectedStore.hash_id.substring(0, 8)}`;
      setBitlyLink(shortUrl);
      
      // Copy to clipboard
      await navigator.clipboard.writeText(shortUrl);
      alert('Shortened link copied to clipboard!');
    } catch (err) {
      console.error('Error generating link:', err);
      alert('Failed to generate shortened link');
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const copyEmbedCode = async () => {
    if (!selectedStore?.hash_id) return;
    
    const embedCode = `<iframe src="https://app.vivvy.ai/checkin/${selectedStore.hash_id}?fullscreen=true" width="100%" height="600" frameborder="0" style="border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);"></iframe>`;
    
    try {
      await navigator.clipboard.writeText(embedCode);
      alert('Embed code copied to clipboard!');
    } catch (err) {
      console.error('Error copying embed code:', err);
      alert('Failed to copy embed code');
    }
  };

  const copyChatbotLink = async () => {
    if (!selectedStore?.hash_id) return;
    
    const chatbotUrl = `https://app.vivvy.ai/chatbot/${selectedStore.hash_id}`;
    
    try {
      await navigator.clipboard.writeText(chatbotUrl);
      alert('Chatbot link copied to clipboard!');
    } catch (err) {
      console.error('Error copying chatbot link:', err);
      alert('Failed to copy chatbot link');
    }
  };

  const renderPreviewContent = () => (
    <>
      {bgType === "image" ? (
        <img
          src={getBackgroundSource()}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <video
          autoPlay
          loop
          muted
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        >
          <source src={getBackgroundSource()} type="video/mp4" />
        </video>
      )}

      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: getOverlayStyle(),
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          color: fontColor,
          textAlign: 'center',
          padding: '20px'
        }}
      >
        <img
          src={getLogoSource()}
          alt="Logo"
          style={{
            width: '120px',
            marginBottom: '20px',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'
          }}
        />
        <h2
          style={{
            fontSize: '2rem',
            marginBottom: '20px',
            animation: 'fadeInUp 0.8s ease-out',
            color: fontColor,
          }}
        >
          {headline}
        </h2>
        <QRCodeCanvas
          value={`https://app.vivvy.ai/chatbot/${selectedStore?.hash_id}`}
          size={140}
          fgColor={qrColor}
          bgColor="transparent"
          style={{
            filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))'
          }}
        />
        <p
          style={{
            marginTop: '10px',
            fontSize: '0.9rem',
            color: fontColor,
          }}
        >
          Powered by: {clientName || 'Infinity Entry AI'}
        </p>
        {selectedStore && (
          <a
            href={`https://app.vivvy.ai/chatbot/${selectedStore.hash_id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              marginTop: '10px',
              display: 'inline-block',
              color: '#249795',
              textDecoration: 'underline',
              fontWeight: 'bold',
            }}
          >
            Go to Chatbot Page
          </a>
        )}
      </div>
    </>
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="qr-code-designer">
      {/* Header */}
      <div className="designer-header">
        <div className="header-left">
          <h1>🎨 Display Designer</h1>
          <p>Customize your check-in display for {clientName || 'your business'}</p>
        </div>
        <div className="header-right">
          <select 
            value={selectedStore?.id || ''} 
            onChange={handleStoreChange}
            className="store-selector"
            aria-label="Select store for display customization"
          >
            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                📍 {store.store_name}
              </option>
            ))}
          </select>
          <button 
            className="fullscreen-btn"
            onClick={() => {
              if (fullscreen) {
                setFullscreen(false);
                setIsActualFullscreen(false);
                if (document.fullscreenElement) {
                  document.exitFullscreen();
                }
              } else {
                setFullscreen(true);
                // Try to enter actual fullscreen
                if (document.documentElement.requestFullscreen) {
                  document.documentElement.requestFullscreen().then(() => {
                    setIsActualFullscreen(true);
                  }).catch(() => {
                    // Fullscreen failed, continue with overlay
                    setIsActualFullscreen(false);
                  });
                }
              }
            }}
          >
            {fullscreen ? "📱 Exit Fullscreen" : "🖥️ Fullscreen Preview"}
          </button>
          <button 
            className="tv-display-btn"
            onClick={() => {
              const displayUrl = `https://app.vivvy.ai/checkin/${selectedStore?.hash_id}?fullscreen=true&kiosk=true`;
              window.open(displayUrl, '_blank', 'fullscreen=yes,scrollbars=no,resizable=no');
            }}
          >
            📺 Display on TV
          </button>
        </div>
      </div>

      {/* Main Designer */}
      <div className="designer-main">
        {/* Toolbar */}
        <div className="designer-toolbar">
          <div className="toolbar-section">
            <button 
              className={`tool-btn ${activeTool === 'content' ? 'active' : ''}`}
              onClick={() => setActiveTool('content')}
            >
              ✏️ Content
            </button>
            <button 
              className={`tool-btn ${activeTool === 'background' ? 'active' : ''}`}
              onClick={() => setActiveTool('background')}
            >
              🖼️ Background
            </button>
            <button 
              className={`tool-btn ${activeTool === 'colors' ? 'active' : ''}`}
              onClick={() => setActiveTool('colors')}
            >
              🎨 Colors
            </button>
            <button 
              className={`tool-btn ${activeTool === 'logo' ? 'active' : ''}`}
              onClick={() => setActiveTool('logo')}
            >
              🏷️ Logo
            </button>
          </div>
          
          <div className="toolbar-actions">
            <button 
              className="save-btn"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? '💾 Saving...' : '💾 Save Design'}
            </button>
            <button 
              className="export-btn"
              onClick={() => setShowExportTools(!showExportTools)}
            >
              📤 Export & Share
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="designer-content">
          {/* Tool Panels */}
          <div className="tool-panels">
            {/* Content Panel */}
            {activeTool === 'content' && (
              <div className="tool-panel">
                <div className="panel-section">
                  <h3>📝 Headline Text</h3>
                  <input 
                    type="text" 
                    value={headline} 
                    onChange={(e) => setHeadline(e.target.value)}
                    placeholder="Enter your headline..."
                    className="modern-input"
                  />
                  <p className="helper-text">This is the main message customers will see</p>
                </div>
              </div>
            )}

            {/* Background Panel */}
            {activeTool === 'background' && (
              <div className="tool-panel">
                <div className="panel-section">
                  <h3>🖼️ Background Type</h3>
                  <div className="option-group">
                    <button 
                      className={`option-btn ${bgType === 'image' ? 'active' : ''}`}
                      onClick={() => setBgType('image')}
                    >
                      📷 Image
                    </button>
                    <button 
                      className={`option-btn ${bgType === 'video' ? 'active' : ''}`}
                      onClick={() => setBgType('video')}
                    >
                      🎥 Video
                    </button>
                  </div>
                </div>

                <div className="panel-section">
                  <h3>📁 Background Source</h3>
                  <div className="option-group">
                    <button 
                      className={`option-btn ${bgSourceType === 'url' ? 'active' : ''}`}
                      onClick={() => setBgSourceType('url')}
                    >
                      🔗 URL
                    </button>
                    <button 
                      className={`option-btn ${bgSourceType === 'upload' ? 'active' : ''}`}
                      onClick={() => setBgSourceType('upload')}
                    >
                      📤 Upload
                    </button>
                  </div>

                  {bgSourceType === 'url' ? (
                    <input 
                      type="text" 
                      value={bgUrl} 
                      onChange={(e) => setBgUrl(e.target.value)}
                      placeholder={`Enter ${bgType} URL...`}
                      className="modern-input"
                    />
                  ) : (
                    <div className="file-upload-area">
                      <input
                        type="file"
                        accept={bgType === "image" ? "image/*" : "video/*"}
                        onChange={(e) => setBgFile(e.target.files?.[0] || null)}
                        id="bg-upload"
                      />
                      <label htmlFor="bg-upload">
                        📁 Choose {bgType === 'image' ? 'Image' : 'Video'} File
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Colors Panel */}
            {activeTool === 'colors' && (
              <div className="tool-panel">
                <div className="panel-section">
                  <h3>🎨 Overlay Style</h3>
                  <div className="option-group">
                    <button 
                      className={`option-btn ${overlayMode === 'solid' ? 'active' : ''}`}
                      onClick={() => setOverlayMode('solid')}
                    >
                      🎯 Solid
                    </button>
                    <button 
                      className={`option-btn ${overlayMode === 'gradient' ? 'active' : ''}`}
                      onClick={() => setOverlayMode('gradient')}
                    >
                      🌈 Gradient
                    </button>
                  </div>
                </div>

                <div className="panel-section">
                  <h3>🎨 Color Settings</h3>
                  <div className="color-controls">
                    <div className="color-item">
                      <label>Text Color</label>
                      <div className="color-picker-container">
                        <div 
                          className="color-preview"
                          style={{ backgroundColor: fontColor }}
                          onClick={() => setShowColorPicker(showColorPicker === 'font' ? null : 'font')}
                        ></div>
                        {showColorPicker === 'font' && (
                          <div className="color-picker-popup">
                            <ChromePicker
                              color={fontColor}
                              onChange={(color) => setFontColor(`rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`)}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="color-item">
                      <label>QR Code Color</label>
                      <div className="color-picker-container">
                        <div 
                          className="color-preview"
                          style={{ backgroundColor: qrColor }}
                          onClick={() => setShowColorPicker(showColorPicker === 'qr' ? null : 'qr')}
                        ></div>
                        {showColorPicker === 'qr' && (
                          <div className="color-picker-popup">
                            <ChromePicker
                              color={qrColor}
                              onChange={(color) => setQrColor(`rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`)}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {overlayMode === 'solid' ? (
                      <div className="color-item">
                        <label>Overlay Color</label>
                        <div className="color-picker-container">
                          <div 
                            className="color-preview"
                            style={{ backgroundColor: solidColor }}
                            onClick={() => setShowColorPicker(showColorPicker === 'overlay' ? null : 'overlay')}
                          ></div>
                          {showColorPicker === 'overlay' && (
                            <div className="color-picker-popup">
                              <ChromePicker
                                color={solidColor}
                                onChange={(color) => setSolidColor(`rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`)}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="color-item">
                          <label>Gradient Start</label>
                          <div className="color-picker-container">
                            <div 
                              className="color-preview"
                              style={{ backgroundColor: gradientStart }}
                              onClick={() => setShowColorPicker(showColorPicker === 'gradient1' ? null : 'gradient1')}
                            ></div>
                            {showColorPicker === 'gradient1' && (
                              <div className="color-picker-popup">
                                <ChromePicker
                                  color={gradientStart}
                                  onChange={(color) => setGradientStart(`rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`)}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="color-item">
                          <label>Gradient End</label>
                          <div className="color-picker-container">
                            <div 
                              className="color-preview"
                              style={{ backgroundColor: gradientEnd }}
                              onClick={() => setShowColorPicker(showColorPicker === 'gradient2' ? null : 'gradient2')}
                            ></div>
                            {showColorPicker === 'gradient2' && (
                              <div className="color-picker-popup">
                                <ChromePicker
                                  color={gradientEnd}
                                  onChange={(color) => setGradientEnd(`rgba(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b}, ${color.rgb.a})`)}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Logo Panel */}
            {activeTool === 'logo' && (
              <div className="tool-panel">
                <div className="panel-section">
                  <h3>🏷️ Logo Settings</h3>
                  <div className="option-group">
                    <button 
                      className={`option-btn ${logoSourceType === 'url' ? 'active' : ''}`}
                      onClick={() => setLogoSourceType('url')}
                    >
                      🔗 URL
                    </button>
                    <button 
                      className={`option-btn ${logoSourceType === 'upload' ? 'active' : ''}`}
                      onClick={() => setLogoSourceType('upload')}
                    >
                      📤 Upload
                    </button>
                  </div>

                  {logoSourceType === 'url' ? (
                    <input 
                      type="text" 
                      value={logoUrl} 
                      onChange={(e) => setLogoUrl(e.target.value)}
                      placeholder="Enter logo URL..."
                      className="modern-input"
                    />
                  ) : (
                    <div className="file-upload-area">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                        id="logo-upload"
                      />
                      <label htmlFor="logo-upload">
                        📁 Choose Logo File
                      </label>
                    </div>
                  )}
                </div>

                {getLogoSource() && (
                  <div className="panel-section">
                    <h3>👀 Logo Preview</h3>
                    <div className="logo-preview">
                      <img 
                        src={getLogoSource()} 
                        alt="Logo Preview" 
                        style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Export Tools Panel */}
          {showExportTools && (
            <div className="export-panel">
              <div className="export-header">
                <h3>📤 Export & Share Tools</h3>
                <button 
                  className="close-export"
                  onClick={() => setShowExportTools(false)}
                >
                  ✕
                </button>
              </div>
              
              <div className="export-tools">
                <div className="export-section">
                  <h4>🎯 QR Code</h4>
                  <button className="export-btn-secondary" onClick={downloadQRCode}>
                    📥 Download QR as PNG
                  </button>
                </div>
                
                <div className="export-section">
                  <h4>🔗 Links</h4>
                  <button className="export-btn-secondary" onClick={generateBitlyLink} disabled={isGeneratingLink}>
                    {isGeneratingLink ? '⏳ Generating...' : '🔗 Generate Short Link'}
                  </button>
                  {bitlyLink && (
                    <div className="generated-link">
                      <span>{bitlyLink}</span>
                    </div>
                  )}
                  <button className="export-btn-secondary" onClick={copyChatbotLink}>
                    🤖 Copy Chatbot Link
                  </button>
                </div>
                
                <div className="export-section">
                  <h4>📋 Embed</h4>
                  <button className="export-btn-secondary" onClick={copyEmbedCode}>
                    📋 Copy iFrame Code
                  </button>
                  <div className="embed-preview">
                    <code>
                      &lt;iframe src="https://app.vivvy.ai/checkin/{selectedStore?.hash_id}?fullscreen=true" width="100%" height="600" frameborder="0"&gt;&lt;/iframe&gt;
                    </code>
                  </div>
                </div>
                
                <div className="export-section">
                  <h4>📺 Display Instructions</h4>
                  <div className="display-instructions">
                    <p><strong>Smart TV:</strong> Open browser, navigate to the check-in URL</p>
                    <p><strong>Fire Stick/Roku:</strong> Use Silk Browser or Web Browser X</p>
                    <p><strong>Computer:</strong> Connect via HDMI, use "Display on TV" button</p>
                    <p><strong>Tablet:</strong> Mount on stand, use fullscreen mode</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Preview Area */}
          <div className="preview-area">
            <div className={`preview-header ${showPreviewControls ? 'visible' : 'hidden'}`}>
              <h3>📱 Live Preview</h3>
              <div className="preview-controls">
                <span className="preview-label">Your display will look like this:</span>
              </div>
            </div>
            <div className="preview-container">
              <div className="checkin-preview">
                {renderPreviewContent()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Overlay */}
      {fullscreen && (
        <div className={`fullscreen-overlay ${isActualFullscreen ? 'actual-fullscreen' : ''}`}>
          <div className="fullscreen-header">
            <h2>🖥️ Fullscreen Preview</h2>
            <div className="fullscreen-controls">
              <button 
                className="fullscreen-toggle-btn"
                onClick={() => {
                  if (document.fullscreenElement) {
                    document.exitFullscreen();
                    setIsActualFullscreen(false);
                  } else {
                    document.documentElement.requestFullscreen().then(() => {
                      setIsActualFullscreen(true);
                    }).catch(() => {
                      setIsActualFullscreen(false);
                    });
                  }
                }}
              >
                {document.fullscreenElement ? '📱 Exit Fullscreen' : '🖥️ Enter Fullscreen'}
              </button>
              <button 
                className="close-fullscreen"
                onClick={() => {
                  setFullscreen(false);
                  setIsActualFullscreen(false);
                  if (document.fullscreenElement) {
                    document.exitFullscreen();
                  }
                }}
              >
                ✕ Close Preview
              </button>
            </div>
          </div>
          <div className="checkin-preview fullscreen-true">
            {renderPreviewContent()}
          </div>
        </div>
      )}


    </div>
  );
};

export default QRCodeComponent;
